<template>
  <section class="receipts-list">
    <dr-loader
      v-if="ui.isLoading"
      size="lg"
      class="m-auto p-8"
    />

    <p
      v-if="!ui.isLoading && data.length === 0"
      class="text-base py-3 pb-0"
    >
      Nenhum recibo encontrado.
    </p>

    <table
      v-if="!ui.isLoading && data.length > 0"
      class="receipts-list__table"
    >
      <thead>
        <tr>
          <th
            v-for="(item, index) in header"
            :key="`receipt-header-${index}`"
          >
            {{ item.name }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(row, index) in data"
          :key="`receipt-row-${index}`"
        >
          <td
            v-for="(item, j) in row.data"
            :key="`receipt-data-${j}`"
          >
            <template v-if="item.value">
              {{ item.value }}
            </template>

            <template v-if="!item.value">
              <div
                class="cursor-pointer flex flex-row items-center justify-center w-auto text-primary"
                @click="getReceiptData(row.id)"
              >
                <dr-icon
                  fill="#56BDD2"
                  color="#56BDD2"
                  name="receipt"
                  class="h-4 w-4 mr-2"
                />

                <span>
                  ver recibo
                </span>
              </div>
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-if="showPaginationButton"
      class="w-full flex items-center justify-center p-4"
    >
      <button
        :disabled="ui.isFetching"
        :class="{ 'cursor-not-allowed': ui.isFetching }"
        class="btn btn-blue !h-8 !flex !items-center !justify-center !text-sm"
        @click="getReceipts"
      >
        Mostrar mais
      </button>
    </div>

    <transition
      name="fade"
      mode="out-in"
    >
      <receipt-details
        v-if="ui.showReceipt"
        :receipt.sync="receipt"
        @close="ui.showReceipt = false"
      />
    </transition>
  </section>
</template>

<script>
import receiptDetails from './receiptDetails.vue'

export default {
  name: 'PlansReceiptsList',

  props: {
    hasPagination: {
      type: Boolean,
      required: false,
      default: () => true
    },

    maxLines: {
      type: Number,
      required: false,
      default: () => 10
    }
  },

  components: {
    receiptDetails
  },

  data () {
    return {
      header: [
        { name: 'Data', order: 1 },
        { name: 'Valor', order: 2 },
        { name: 'Forma de pagamento', order: 3 },
        { name: '', order: 4 }
      ],
      data: [],
      pagination: null,
      ui: {
        showReceipt: false,
        isLoading: false,
        isFetching: false
      },
      receipt: null,
      financialCategoryId: 0
    }
  },

  computed: {
    showPaginationButton () {
      if (!this.hasPagination) {
        return false
      }

      return this.hasPagination &&
        this.pagination &&
        this.pagination.currentPage < this.pagination.totalPages
    }
  },

  created () {
    this.getFinancialCategories()
      .then(this.getReceipts)
  },

  methods: {
    getFinancialCategories () {
      return new Promise((resolve, reject) => {
        const url = '/financial_category'

        this.$http
          .get(url, this.$store.getters.getAuthHeader)
          .then((res) => {
            this.financialCategoryId = res.data
              .find((item) => /.*(assinatura).*/gi.test(item.name))
              .id

            resolve()
          })
          .catch(reject)
      })
    },

    getReceipts () {
      const page = this.pagination
        ? this.pagination.currentPage + 1
        : 1

      const filters = [
        { filter: 'itensPerPage', value: this.maxLines },
        { filter: 'page', value: page },
        { filter: 'order[fs.date]', value: 'desc' },
        // { filter: 'filterAndLike[fs.isConsolidated]', value: '1' },
        { filter: 'filterIn[fs.financialCategory][]', value: this.financialCategoryId }
      ]

      const queryString = filters
        .filter((item) => item.value)
        .reduce(
          (acc, item) => `${String(acc).startsWith('?') ? `${acc}&` : '?'}${item.filter}=${item.value}`,
          ''
        )

      const url = `/doctor/financial_statement${queryString}`

      this.ui.isLoading = page === 1
      this.ui.isFetching = true
      this.fn_SHOW_PROGRESS()
      this.$http
        .get(url, this.$store.getters.getAuthHeader)
        .then((res) => {
          const { data, pagination } = res.data

          const formattedData = data.map((item) => {
            const { date, 'payment_method': paymentMethod, value, id } = item

            const toBRL = (str) => {
              const money = Number(str).toFixed(2)

              return `R$ ${money.replace(/^(\d{1,})\.(\d{1,2})$/gmi, '$1,$2')}`
            }

            return {
              id,
              data: [
                { value: this.$moment(date).format('DD/MM/YYYY hh:mm'), order: 1 },
                { value: toBRL(value), order: 2 },
                { value: paymentMethod || ' ', order: 3 },
                { value: null, order: 4 }
              ]
            }
          })

          this.data = [...this.data, ...formattedData]
          this.pagination = pagination
        })
        .finally(() => {
          this.ui.isLoading = false
          this.ui.isFetching = false
          this.fn_HIDE_PROGRESS()
        })
    },

    getReceiptData (id) {
      const { data: receipt } = this.data.find((item) => item.id === id)
      const obj = []

      for (const header of this.header) {
        const { value } = receipt.find((item) => item.order === header.order)
        const { name: key } = header

        if (value) {
          obj.push({ key, value })
        }
      }

      this.receipt = obj
      this.ui.showReceipt = true
    }
  }
}
</script>

<style lang="scss" scoped>
.receipts-list {
  &__table {
    @apply w-full;

    thead tr th {
      @apply p-3 text-sm font-bold uppercase;
      background: #eeeeee;
      text-align: left;
      width: calc(100% / 5);
    }
  }

  tbody tr {
    border-top: 1px solid #DDDDDD;

    &:last-of-type {
      border-bottom: 1px solid #dddddd;
    }

    &:hover {
      td {
        background-color: #f5f5f5;
      }
    }

    td {
      @apply p-3;
      background: white;
    }
  }
}
</style>
